import * as React from "react";
import { Droplets, FlaskConical, Heart, Thermometer } from "lucide-react";
import { cn, formatTemperature, isPeakMucus, isPositiveOvulationTest } from "@nfp-tracker/utils";
import { SYMPTOM_OPTIONS } from "./symptom-options";

export interface ObservationCellData {
  bleeding: number | null;
  monitor: string | null;
  intercourse: boolean;
  mucus: string | null;
  mucus_consistency?: string | null;
  mucus_color?: string | null;
  symptoms: string[] | null;
  temperature_f: number | null;
  /** OPK / LH strip result: "positive" | "negative" | null (not tested). */
  ovulation_test?: string | null;
}

export type ObservationCellSize = "sm" | "lg";

export interface IconTypeOption {
  key: string;
  label: string;
}

/** Icon-row categories, in the order they render. Toggleable in the key; visible by default. */
export const ICON_TYPE_OPTIONS: IconTypeOption[] = [
  { key: "bleeding", label: "Bleeding" },
  { key: "monitor", label: "Monitor (Peak/High/Low)" },
  { key: "ovulation_test", label: "Ovulation test (OPK)" },
  { key: "temperature", label: "Temperature" },
  { key: "mucus", label: "Mucus" },
  { key: "intercourse", label: "Intercourse" },
];

export const DEFAULT_VISIBLE_ICON_TYPES = ICON_TYPE_OPTIONS.map((option) => option.key);

const getMonitorBadgeClass = (monitor?: string | null) => {
  const value = (monitor || "").toUpperCase();
  if (value === "PEAK") return "bg-violet-700 text-white";
  if (value === "HIGH") return "bg-blue-700 text-white";
  return "bg-slate-700 text-white";
};

const NEUTRAL_CELL_STYLE = "border-border bg-muted/40 text-muted-foreground";

/** Bleeding intensity, light to heavy — the only three shades this color family uses. */
const BLEEDING_STYLE_BY_LEVEL: Record<number, string> = {
  1: "border-red-300/70 bg-red-300/20 text-red-700",
  2: "border-red-500/70 bg-red-500/25 text-red-700",
  3: "border-red-800/70 bg-red-800/30 text-red-900",
};

/** Mucus seen more than 3 days past the identified Peak Day — not interpreted as fertile,
 * even if it looks peak-type, so it gets a distinct muted style instead of the green wash. */
export const POST_PEAK_NON_FERTILE_STYLE = "border-slate-400/60 bg-slate-400/20 text-slate-700";

/**
 * Background wash is reserved for the two signals worth scanning a whole cycle for at a
 * glance — bleeding (shaded by intensity) and the fertile window. Everything else
 * (intercourse, temperature, mucus, monitor, symptoms) is an icon, never a background,
 * so it can't be mistaken for one of those two states or blot one out.
 */
export const getObservationCellStyle = (
  observation?: ObservationCellData | null,
  options?: { isPostPeakNonFertile?: boolean }
) => {
  if (!observation) {
    return NEUTRAL_CELL_STYLE;
  }

  if (observation.bleeding) {
    return BLEEDING_STYLE_BY_LEVEL[observation.bleeding] ?? BLEEDING_STYLE_BY_LEVEL[3];
  }

  const monitor = (observation.monitor || "").toLowerCase();
  const isMonitorPeakSignal = monitor.includes("peak") || monitor.includes("high");
  const isPeakMucusSignal = isPeakMucus(
    observation.mucus_consistency,
    observation.mucus,
    observation.mucus_color
  );

  if (options?.isPostPeakNonFertile && isPeakMucusSignal && !isMonitorPeakSignal) {
    return POST_PEAK_NON_FERTILE_STYLE;
  }

  if (isMonitorPeakSignal || isPeakMucusSignal) {
    return "border-green-600/60 bg-green-500/20 text-green-700";
  }

  return NEUTRAL_CELL_STYLE;
};

/** Ring treatment layered on top of a cell's base style when a symptom filter is active. Non-matching cells keep their normal styling. */
export const getSymptomHighlightClass = (
  observation: Pick<ObservationCellData, "symptoms"> | null | undefined,
  activeSymptomFilters: string[] = []
) => {
  if (!activeSymptomFilters.length) return "";
  const matches = Boolean(
    observation?.symptoms?.some((symptom) => activeSymptomFilters.includes(symptom))
  );
  return matches ? "ring-2 ring-amber-500 ring-offset-1" : "";
};

const IconChip = ({
  size,
  className,
  title,
  children,
}: {
  size: ObservationCellSize;
  className: string;
  title?: string;
  children: React.ReactNode;
}) => (
  <span
    title={title}
    className={cn(
      "inline-flex items-center justify-center rounded-full shadow-sm",
      size === "sm" ? "h-2.5 w-2.5" : "h-4 w-4",
      className
    )}
  >
    {children}
  </span>
);

export interface ObservationCellIconsProps {
  observation?: ObservationCellData | null;
  size?: ObservationCellSize;
  /** Icon-row categories to render. Defaults to all (see ICON_TYPE_OPTIONS). */
  visibleTypes?: string[];
  /** Symptom names to surface as icons. Defaults to none (declutter by default). */
  activeSymptomFilters?: string[];
}

/** Icon-chip row summarizing a day's observation. Shared between the cycle grid (sm) and month grid (lg). */
export const ObservationCellIcons = ({
  observation,
  size = "sm",
  visibleTypes = DEFAULT_VISIBLE_ICON_TYPES,
  activeSymptomFilters = [],
}: ObservationCellIconsProps) => {
  if (!observation) {
    return null;
  }

  const iconSize = size === "sm" ? "h-2 w-2" : "h-3 w-3";
  const matchedSymptoms = activeSymptomFilters.filter((symptom) =>
    observation.symptoms?.includes(symptom)
  );

  return (
    <div
      className={cn(
        "flex flex-wrap max-w-full items-center justify-center gap-0.5 overflow-hidden leading-none",
        size === "sm" ? "mt-0.5 w-9 text-[10px]" : "mt-1 text-xs"
      )}
    >
      {observation.bleeding && visibleTypes.includes("bleeding") ? (
        <IconChip size={size} className="bg-red-600 text-white" title={`Bleeding: ${observation.bleeding}`}>
          <Droplets className={iconSize} />
        </IconChip>
      ) : null}
      {observation.monitor && visibleTypes.includes("monitor") ? (
        <span
          title={`Monitor: ${observation.monitor}`}
          className={cn(
            "inline-flex items-center justify-center rounded-full font-bold leading-none shadow-sm",
            size === "sm" ? "h-2.5 min-w-2.5 px-0.5 text-[7px]" : "h-4 min-w-4 px-1 text-[9px]",
            getMonitorBadgeClass(observation.monitor)
          )}
        >
          {observation.monitor.charAt(0).toUpperCase()}
        </span>
      ) : null}
      {isPositiveOvulationTest(observation.ovulation_test) &&
      visibleTypes.includes("ovulation_test") ? (
        <IconChip size={size} className="bg-fuchsia-600 text-white" title="Ovulation test: positive">
          <FlaskConical className={iconSize} />
        </IconChip>
      ) : null}
      {observation.temperature_f && visibleTypes.includes("temperature") ? (
        <IconChip
          size={size}
          className="bg-emerald-700 text-white"
          title={`Temperature: ${formatTemperature(observation.temperature_f, "°F")}`}
        >
          <Thermometer className={iconSize} />
        </IconChip>
      ) : null}
      {observation.mucus && visibleTypes.includes("mucus") ? (
        <IconChip size={size} className="bg-teal-500 text-white" title={`Mucus: ${observation.mucus}`}>
          <Droplets className={iconSize} />
        </IconChip>
      ) : null}
      {observation.intercourse && visibleTypes.includes("intercourse") ? (
        <IconChip size={size} className="bg-pink-700 text-white" title="Intercourse logged">
          <Heart className={iconSize} />
        </IconChip>
      ) : null}
      {matchedSymptoms.map((symptom) => {
        const option = SYMPTOM_OPTIONS.find((candidate) => candidate.name === symptom);
        if (!option) return null;
        const SymptomIcon = option.icon;
        return (
          <IconChip key={symptom} size={size} className="bg-amber-500 text-black" title={symptom}>
            <SymptomIcon className={iconSize} />
          </IconChip>
        );
      })}
    </div>
  );
};
